import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { $getRoot, $insertNodes, EditorState, TextNode } from "lexical";
import { useEffect, useId, useState } from "react";
import { CustomAutoLinkNode, CustomLinkNode } from "./nodes/CustomLinkNode";

import { CustomTableCellNode } from "./nodes/CustomTableCellNode";
import { ExtendedTextNode } from "./nodes/ExtendedTextNode";
import { ImageNode } from "./nodes/ImageNode";
import { CustomLinkPlugin } from "./plugins/CustomLinkPlugin";
import { FloatingLinkUIPlugin } from "./plugins/FloatingLinkUIPlugin";
import FontSizePlugin from "./plugins/FontSizePlugin";
import HighlightColorPlugin from "./plugins/HighlightColorPlugin";
import ImagePlugin from "./plugins/ImagePlugin";
import TableActionMenuPlugin from "./plugins/TableActionMenuPlugin";
import TableCellResizerPlugin from "./plugins/TableCellResizerPlugin";
import TablePlugin from "./plugins/TablePlugin";
import TextColorPlugin from "./plugins/TextColorPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";

import "./TextEditor.scss";

type Props = {
  id?: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  showToolbar?: boolean;
  minimalMode?: boolean;
};

function OnChangeHandler({ onChange }: { onChange: (value: string) => void }) {
  const [editor] = useLexicalComposerContext();

  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor);
      onChange(htmlString);
    });
  };

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
}

function InitialContentPlugin({ initialContent }: { initialContent: string }) {
  const [editor] = useLexicalComposerContext();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && initialContent) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(initialContent, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        $insertNodes(nodes);
      });
      setInitialized(true);
    }
  }, [editor, initialContent, initialized]);

  return null;
}

function TextEditor({
  id = "texteditor",
  value,
  placeholder = "",
  onChange,
  showToolbar = true,
  minimalMode = false
}: Props) {
  const uniqueId = useId();
  const toolbarId = `${id}-toolbar${uniqueId}`;

  const theme = {
    text: {
      bold: "editor-text-bold",
      italic: "editor-text-italic",
      underline: "editor-text-underline"
    },
    link: "editor-link",
    list: {
      ul: "editor-list-ul",
      ol: "editor-list-ol",
      listitem: "editor-listitem"
    },
    image: "editor-image"
  };

  function onError(error: Error) {
    console.error("Lexical error:", error);
  }

  const initialConfig = {
    namespace: id,
    theme,
    onError,
    nodes: [
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
        withKlass: ExtendedTextNode
      },
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      CustomLinkNode,
      {
        replace: LinkNode,
        with: (node: LinkNode) => {
          return new CustomLinkNode(node.getURL(), {
            rel: node.getRel() ?? undefined,
            target: node.getTarget() ?? undefined,
            title: node.getTitle() ?? undefined
          });
        },
        withKlass: CustomLinkNode
      },
      CustomAutoLinkNode,
      {
        replace: AutoLinkNode,
        with: (node: AutoLinkNode) => {
          return new CustomAutoLinkNode(node.getURL(), {
            rel: node.getRel() ?? undefined,
            target: node.getTarget() ?? undefined,
            title: node.getTitle() ?? undefined
          });
        },
        withKlass: CustomAutoLinkNode
      },
      ImageNode,
      TableNode,
      CustomTableCellNode,
      {
        replace: TableCellNode,
        with: (node: TableCellNode) => {
          return new CustomTableCellNode(
            node.getHeaderStyles(),
            node.getColSpan(),
            node.getWidth()
          );
        },
        withKlass: CustomTableCellNode
      },
      TableRowNode
    ]
  };

  return (
    <div className="text-editor">
      <LexicalComposer initialConfig={initialConfig}>
        {showToolbar && <ToolbarPlugin toolbarId={toolbarId} minimalMode={minimalMode} />}

        <RichTextPlugin
          contentEditable={
            <ContentEditable
              id={id}
              className="editor-input"
              aria-placeholder={placeholder}
              placeholder={
                <div className="editor-placeholder">{placeholder}</div>
              }
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />

        <HistoryPlugin />
        <CustomLinkPlugin />
        <FloatingLinkUIPlugin />
        <ListPlugin />
        <ImagePlugin />
        <FontSizePlugin />
        <TextColorPlugin />
        <HighlightColorPlugin />
        <TablePlugin />
        <TableActionMenuPlugin />
        <TableCellResizerPlugin />
        <OnChangeHandler onChange={onChange} />
        <InitialContentPlugin initialContent={value} />
      </LexicalComposer>
    </div>
  );
}

export default TextEditor;
